// shell/components/selection/selection-manager.ts

import { IDisposable, SelectionState } from '../types';
import { DisposableStore } from '../disposable';

/**
 * Configuration for the SelectionManager component.
 */
export interface SelectionManagerConfig<T> {
    /** Extract unique key from item */
    keyFn: (item: T) => string;

    /** Selection mode */
    mode: 'none' | 'single' | 'multi';

    /** Called when selection changes */
    onSelectionChange?: (state: SelectionState<T>) => void;

    /** Predicate for selectable items (e.g., disable selection on certain rows) */
    canSelect?: (item: T) => boolean;

    /** What does "select all" mean? */
    selectAllBehavior?: 'loaded' | 'visible' | 'all';

    /** Required when selectAllBehavior is 'all' */
    getTotalCount?: () => number;

    /**
     * Optional: retrieve item by key (for anchor handling when anchor is off-screen).
     * If provided, enables proper range selection across paginated data.
     */
    getItemByKey?: (key: string) => T | undefined;
}

/**
 * SelectionManager handles multi-selection state with keyboard and mouse interactions.
 *
 * Key features:
 * - Tracks selection by key for O(1) lookups
 * - Supports single and multi-selection modes
 * - Handles range selection with Shift+Click
 * - Provides "select all" with exclusion tracking for large datasets
 * - Emits selection state as a discriminated union
 *
 * Design decisions:
 * - Pure state management - no DOM rendering
 * - Range selection replaces previous range (not additive)
 * - Anchor persists across scroll/pagination for consistent Shift+Click behavior
 */
export class SelectionManager<T> implements IDisposable {
    private readonly config: SelectionManagerConfig<T>;
    private readonly keyFn: (item: T) => string;
    private readonly mode: 'none' | 'single' | 'multi';
    private readonly disposables = new DisposableStore();

    // Selection state
    private selectedKeys = new Set<string>();
    private selectedItems = new Map<string, T>();

    // For range selection
    private anchorKey: string | null = null;
    private lastRangeKeys = new Set<string>();

    // "Select all" state for 'all' behavior
    private allSelected = false;
    private excludedKeys = new Set<string>();

    constructor(config: SelectionManagerConfig<T>) {
        this.config = config;
        this.keyFn = config.keyFn;
        this.mode = config.mode;
    }

    // ===== State Queries =====

    /**
     * Get all selected items.
     */
    public getSelected(): T[] {
        return Array.from(this.selectedItems.values());
    }

    /**
     * Get all selected keys.
     */
    public getSelectedKeys(): Set<string> {
        return new Set(this.selectedKeys);
    }

    /**
     * Get the count of selected items.
     */
    public getSelectionCount(): number {
        if (this.allSelected) {
            const totalCount = this.config.getTotalCount?.() ?? 0;
            return totalCount - this.excludedKeys.size;
        }
        return this.selectedKeys.size;
    }

    /**
     * Get the selection state as a discriminated union.
     */
    public getSelectionState(): SelectionState<T> {
        if (this.mode === 'none') {
            return { mode: 'none' };
        }

        if (this.allSelected) {
            return {
                mode: 'all',
                excludedKeys: new Set(this.excludedKeys),
                totalCount: this.config.getTotalCount?.() ?? 0
            };
        }

        if (this.selectedKeys.size === 0) {
            return { mode: 'none' };
        }

        return {
            mode: 'explicit',
            items: Array.from(this.selectedItems.values()),
            keys: new Set(this.selectedKeys)
        };
    }

    /**
     * Check if an item is selected.
     */
    public isSelected(item: T): boolean {
        const key = this.keyFn(item);

        if (this.allSelected) {
            return !this.excludedKeys.has(key) && this.canSelectItem(item);
        }

        return this.selectedKeys.has(key);
    }

    /**
     * Check if a key is selected.
     */
    public isSelectedByKey(key: string): boolean {
        if (this.allSelected) {
            return !this.excludedKeys.has(key);
        }
        return this.selectedKeys.has(key);
    }

    /**
     * Check if "all" mode is active.
     */
    public isAllSelected(): boolean {
        return this.allSelected;
    }

    /**
     * Get excluded keys (for 'all' mode).
     */
    public getExcludedKeys(): Set<string> {
        return new Set(this.excludedKeys);
    }

    // ===== Basic Operations =====

    /**
     * Select an item.
     */
    public select(item: T): void {
        if (!this.canSelectItem(item)) return;

        const key = this.keyFn(item);

        if (this.mode === 'single') {
            this.selectedKeys.clear();
            this.selectedItems.clear();
        }

        this.selectedKeys.add(key);
        this.selectedItems.set(key, item);
        this.allSelected = false;
        this.excludedKeys.clear();
        this.lastRangeKeys.clear();

        this.emitChange();
    }

    /**
     * Deselect an item.
     */
    public deselect(item: T): void {
        const key = this.keyFn(item);

        if (this.allSelected) {
            this.excludedKeys.add(key);
        } else {
            this.selectedKeys.delete(key);
            this.selectedItems.delete(key);
        }

        this.lastRangeKeys.clear();
        this.emitChange();
    }

    /**
     * Toggle selection of an item.
     */
    public toggle(item: T): void {
        if (!this.canSelectItem(item)) return;

        const key = this.keyFn(item);

        if (this.allSelected) {
            // Toggle exclusion instead of individual selection
            if (this.excludedKeys.has(key)) {
                this.excludedKeys.delete(key);
            } else {
                this.excludedKeys.add(key);
            }
        } else {
            if (this.selectedKeys.has(key)) {
                this.selectedKeys.delete(key);
                this.selectedItems.delete(key);
            } else {
                if (this.mode === 'single') {
                    this.selectedKeys.clear();
                    this.selectedItems.clear();
                }
                this.selectedKeys.add(key);
                this.selectedItems.set(key, item);
            }
        }

        this.lastRangeKeys.clear();
        this.emitChange();
    }

    /**
     * Clear all selections.
     */
    public clear(): void {
        this.selectedKeys.clear();
        this.selectedItems.clear();
        this.lastRangeKeys.clear();
        this.allSelected = false;
        this.excludedKeys.clear();
        this.anchorKey = null;
        this.emitChange();
    }

    /**
     * Select all items.
     */
    public selectAll(items: T[]): void {
        if (this.mode !== 'multi') return;

        switch (this.config.selectAllBehavior ?? 'loaded') {
            case 'loaded':
            case 'visible':
                this.selectedKeys.clear();
                this.selectedItems.clear();
                for (const item of items) {
                    if (this.canSelectItem(item)) {
                        const key = this.keyFn(item);
                        this.selectedKeys.add(key);
                        this.selectedItems.set(key, item);
                    }
                }
                this.allSelected = false;
                this.excludedKeys.clear();
                break;

            case 'all':
                this.allSelected = true;
                this.excludedKeys.clear();
                this.selectedKeys.clear();
                this.selectedItems.clear();
                break;
        }

        this.lastRangeKeys.clear();
        this.emitChange();
    }

    // ===== Range Selection =====

    /**
     * Select a range of items. Replaces previous range.
     */
    public selectRange(from: T, to: T, allItems: T[]): void {
        if (this.mode !== 'multi') return;

        const fromKey = this.keyFn(from);
        const toKey = this.keyFn(to);

        // Find indices
        let fromIndex = -1;
        let toIndex = -1;

        for (let i = 0; i < allItems.length; i++) {
            const key = this.keyFn(allItems[i]);
            if (key === fromKey) fromIndex = i;
            if (key === toKey) toIndex = i;
            if (fromIndex >= 0 && toIndex >= 0) break;
        }

        if (fromIndex < 0 || toIndex < 0) return;

        // Ensure correct order
        const [start, end] = fromIndex <= toIndex
            ? [fromIndex, toIndex]
            : [toIndex, fromIndex];

        // Build new range FIRST (before clearing old)
        const newRangeKeys = new Set<string>();
        const newRangeItems = new Map<string, T>();

        for (let i = start; i <= end; i++) {
            const item = allItems[i];
            if (this.canSelectItem(item)) {
                const key = this.keyFn(item);
                newRangeKeys.add(key);
                newRangeItems.set(key, item);
            }
        }

        // Now safe to clear previous range
        for (const key of this.lastRangeKeys) {
            // Only remove if not in new range (handles overlapping ranges)
            if (!newRangeKeys.has(key)) {
                this.selectedKeys.delete(key);
                this.selectedItems.delete(key);
            }
        }

        // Add new range items
        for (const [key, item] of newRangeItems) {
            this.selectedKeys.add(key);
            this.selectedItems.set(key, item);
        }

        this.lastRangeKeys = newRangeKeys;
        this.allSelected = false;
        this.excludedKeys.clear();

        this.emitChange();
    }

    /**
     * Set the anchor for range selection.
     */
    public setAnchor(item: T): void {
        this.anchorKey = this.keyFn(item);
    }

    /**
     * Get the anchor item (if available in selected items).
     */
    public getAnchor(): T | undefined {
        if (!this.anchorKey) return undefined;
        return this.selectedItems.get(this.anchorKey);
    }

    /**
     * Get the anchor key.
     */
    public getAnchorKey(): string | null {
        return this.anchorKey;
    }

    // ===== Keyboard Handler =====

    /**
     * Handle keyboard events for selection.
     * Returns true if the event was handled.
     */
    public handleKeyDown(
        event: KeyboardEvent,
        focusedItem: T,
        allItems: T[]
    ): boolean {
        if (this.mode === 'none') return false;

        const focusedKey = this.keyFn(focusedItem);

        switch (event.key) {
            case ' ': // Space - toggle selection
                event.preventDefault();
                if (event.ctrlKey || event.metaKey) {
                    // Ctrl+Space - toggle without affecting anchor
                    if (this.canSelectItem(focusedItem)) {
                        this.toggle(focusedItem);
                    }
                } else {
                    // Plain Space - toggle and set as anchor
                    if (this.canSelectItem(focusedItem)) {
                        this.toggle(focusedItem);
                        this.anchorKey = focusedKey;
                    }
                }
                return true;

            case 'a':
                if (event.ctrlKey || event.metaKey) {
                    event.preventDefault();
                    this.selectAll(allItems);
                    return true;
                }
                return false;

            case 'Escape':
                if (this.selectedKeys.size > 0 || this.allSelected) {
                    event.preventDefault();
                    this.clear();
                    return true;
                }
                return false;

            case 'ArrowUp':
            case 'ArrowDown':
            case 'ArrowLeft':
            case 'ArrowRight':
                // Shift+Arrow extends selection
                if (event.shiftKey && this.mode === 'multi') {
                    if (this.canSelectItem(focusedItem)) {
                        this.extendSelectionToFocused(focusedItem, allItems);
                    }
                    // Don't prevent default - let focus manager handle movement
                    return false;
                }
                return false;

            default:
                return false;
        }
    }

    // ===== Lifecycle =====

    /**
     * Called when filters change.
     */
    public onFiltersChanged(clearSelection = true): void {
        if (clearSelection) {
            this.clear();
        }
    }

    /**
     * Reconcile selection state with current data.
     * Removes selections for keys that no longer exist.
     */
    public reconcile(
        currentKeys: Set<string>,
        updatedItems?: Map<string, T>
    ): void {
        let changed = false;

        // Remove selections for keys that no longer exist
        for (const key of this.selectedKeys) {
            if (!currentKeys.has(key)) {
                this.selectedKeys.delete(key);
                this.selectedItems.delete(key);
                this.lastRangeKeys.delete(key);
                changed = true;
            } else if (updatedItems?.has(key)) {
                // Update item reference
                this.selectedItems.set(key, updatedItems.get(key)!);
            }
        }

        // Clean up exclusions for non-existent keys
        for (const key of this.excludedKeys) {
            if (!currentKeys.has(key)) {
                this.excludedKeys.delete(key);
                changed = true;
            }
        }

        // Clear anchor if it no longer exists
        if (this.anchorKey && !currentKeys.has(this.anchorKey)) {
            this.anchorKey = null;
            changed = true;
        }

        if (changed) {
            this.emitChange();
        }
    }

    /**
     * Update item reference for a selected item.
     * Use when item data changes but key remains the same.
     */
    public updateSelectedItem(key: string, item: T): void {
        if (this.selectedItems.has(key)) {
            this.selectedItems.set(key, item);
            // Don't emit change - just updating reference, selection unchanged
        }
    }

    /**
     * Clean up resources.
     */
    public dispose(): void {
        this.selectedKeys.clear();
        this.selectedItems.clear();
        this.lastRangeKeys.clear();
        this.excludedKeys.clear();
        this.disposables.dispose();
    }

    // ===== Private Methods =====

    private canSelectItem(item: T): boolean {
        return this.config.canSelect?.(item) ?? true;
    }

    private extendSelectionToFocused(focusedItem: T, allItems: T[]): void {
        if (!this.anchorKey) {
            // No anchor - set anchor and select focused
            this.anchorKey = this.keyFn(focusedItem);
            this.select(focusedItem);
            return;
        }

        // Try to find anchor item
        let anchorItem: T | undefined;

        // First check visible items
        for (const item of allItems) {
            if (this.keyFn(item) === this.anchorKey) {
                anchorItem = item;
                break;
            }
        }

        // Fall back to getItemByKey if available (for off-screen anchor)
        if (!anchorItem && this.config.getItemByKey) {
            anchorItem = this.config.getItemByKey(this.anchorKey);
        }

        if (!anchorItem) {
            // Anchor truly unavailable - select from focused and update anchor
            const focusedKey = this.keyFn(focusedItem);

            // Clear previous range
            for (const key of this.lastRangeKeys) {
                this.selectedKeys.delete(key);
                this.selectedItems.delete(key);
            }
            this.lastRangeKeys.clear();

            // Select just focused item
            this.selectedKeys.add(focusedKey);
            this.selectedItems.set(focusedKey, focusedItem);
            this.lastRangeKeys.add(focusedKey);

            // Update anchor to focused item
            this.anchorKey = focusedKey;

            this.emitChange();
            return;
        }

        // Normal range selection from anchor to focused
        this.selectRange(anchorItem, focusedItem, allItems);
    }

    private emitChange(): void {
        const state = this.getSelectionState();
        this.config.onSelectionChange?.(state);
    }
}
